class DrawerMenu extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this;
    this.mainLevel = this.el.querySelector('.drawer-menu__contents');
    this.mainContentPanelAccordion = this.el.querySelector('[data-accordion-item][data-accordion-main-panel-content]');
        this.mainContentPanelCheckbox = this.el.querySelector('#panel-main-content');
    this.isOpen = this.el.classList.contains('is-open') || this.el.hasAttribute('open');
  
    this.init();
    this.initDrawerStateObserver();
  }

  init() {
    // These toggle submenu panel
    this.firstLevelItemsWithChildren = this.el.querySelectorAll('.drawer-menu__item__input');
    this.firstLevelItemsWithChildren.forEach((input) => {
      input.addEventListener('change', this.toggleMenuItem.bind(this));
    });

    // These handle closing of navigation
    this.closeButtons = this.el.querySelectorAll('[data-close]');
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', this.closeAllItems.bind(this));
    });

    this.overlay = this.el.querySelector('[data-overlay]');
    this.overlay.addEventListener('click', this.closeAllItems.bind(this));

    // These handle going back to previous panel
    this.secondLevelBackButtons = this.el.querySelectorAll('[data-drawer-submenu-close]');
    this.secondLevelBackButtons.forEach((button) => {
      button.addEventListener('click', this.toggleSubPanel.bind(this));
    });

    // Handle opening of specific subitem from anywhere in the theme
    this.subitemOpeners = document.querySelectorAll('[data-drawer-submenu-open]');
    this.subitemOpeners.forEach((button) => {
      button.addEventListener('click', this.toggleMenuItemFromTheme.bind(this));
    });

    // On desktop, only one L2 item can be open at a time so only one
    // fixed L3 column B panel shows at once.
    this.secondLevelInputs = this.el.querySelectorAll('.drawer-submenu__item__input');
    this.secondLevelInputs.forEach((input) => {
      input.addEventListener('change', this.handleSecondLevelChange.bind(this));
    });

    this.injectSecondaryMenuIntoSubpanels();
  }

  // Clone the secondary nav items (language switcher, secondary links, divider)
  // from .drawer-menu-nav into a footer div inside each L1 submenu panel.
  // CSS shows the footer only on desktop (>=1024px); mobile sees nothing.
  injectSecondaryMenuIntoSubpanels() {
    const nav = this.el.querySelector('.drawer-menu-nav');
    if (!nav) return;

    // Collect direct nav children that are NOT the primary menu, NOT the
    // main-content panel, and NOT noscript.
    const toClone = Array.from(nav.children).filter(el =>
      !el.classList.contains('first-level-menu') &&
      !el.hasAttribute('data-accordion-main-panel-content') &&
      el.tagName !== 'NOSCRIPT'
    );
    if (!toClone.length) return;

    // One non-banner submenu panel per L1 drilldown item.
    const panels = this.el.querySelectorAll(
      '.first-level-menu > .drawer-menu__item.--drilldown' +
      ' > .drawer-submenu__panel-container:not(.--content)' +
      ' > .drawer-submenu__panel'
    );

    panels.forEach((panel, i) => {
      const footer = document.createElement('div');
      footer.className = 'drawer-submenu__secondary-footer';
      const suffix = `-sfx${i}`;

      toClone.forEach(source => {
        const clone = source.cloneNode(true);
        // Make IDs unique so forms and labels still work.
        clone.querySelectorAll('[id]').forEach(el => { el.id += suffix; });
        clone.querySelectorAll('[for]').forEach(el => {
          el.setAttribute('for', el.getAttribute('for') + suffix);
        });
        clone.querySelectorAll('[aria-labelledby]').forEach(el => {
          el.setAttribute('aria-labelledby',
            el.getAttribute('aria-labelledby') + suffix);
        });
        footer.appendChild(clone);
      });

      panel.appendChild(footer);
    });
  }

  handleSecondLevelChange(e) {
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    const changed = e.currentTarget;
    const hasOtherOpen = Array.from(this.secondLevelInputs).some(
      (input) => input !== changed && input.checked
    );
    if (hasOtherOpen) {
      this.el.classList.add('no-transition');
    }
    this.secondLevelInputs.forEach((input) => {
      if (input !== changed && input.checked) {
        input.checked = false;
        const acc = input.closest('[data-accordion-item]');
        if (acc) acc.classList.remove('is-open');
      }
    });
    if (hasOtherOpen) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.el.classList.remove('no-transition');
        });
      });
    }
  }
  
  closeAllItems(event, skipEl) {
    const checkboxes = this.mainLevel.querySelectorAll('input:checked');
    const isSmallScreen = window.matchMedia('(max-width: 820px)').matches;
  
    checkboxes.forEach((checkbox) => {
      if (skipEl && checkbox === skipEl) {
        return;
      }
      checkbox.checked = false;
      const accordion = checkbox.closest('[data-accordion-item]');
      if (accordion) {
        accordion.classList.remove('is-open');
      }
    });
  
    if (!skipEl) {
      this.mainLevel.classList.remove('has-open-submenu');
    }
    
    // Check if drawer is open and no submenus are open, then open main content panel
    this.updateMainContentPanelState();
  }

  toggleMenuItem(scope) {
    const input = scope.currentTarget || scope;
    if (!input) return;

    this.closeAllItems(scope, input);

    const accordion = input.closest('[data-accordion-item]');
    if (!accordion) {
      return;
    }

    accordion.classList.toggle('is-open');

    // Recompute whether any submenu (excluding the main content panel) is open
    const anySubmenuOpen = Array.from(this.mainLevel.querySelectorAll('input:checked'))
      .some((cb) => cb !== this.mainContentPanelCheckbox);
    if (anySubmenuOpen) {
      this.mainLevel.classList.add('has-open-submenu');
    } else {
      this.mainLevel.classList.remove('has-open-submenu');
    }
  }

  toggleMenuItemFromTheme(scope) {
    let target = false;
    const button = scope.currentTarget || scope;
    const url = button.getAttribute('data-drawer-submenu-open');

    for (const input of this.firstLevelItemsWithChildren) {
      if (input.getAttribute('data-href') === url) {
        target = input;
        break;
      }
    }

    if (!target) return;

    // Ensure --site-header-bottom is fresh before the drawer animates in.
    if (window.Woolman && Woolman.Header && typeof Woolman.Header.setHeaderHeightVar === 'function') {
      Woolman.Header.setHeaderHeightVar();
    }

    // If the drawer is already open, skip the open/close toggle and the
    // transitionend wait — just swap items immediately with no delay.
    if (this.el.classList.contains('is-open')) {
      this.el.classList.add('no-transition');
      this.closeAllItems();
      const evt = new Event('change');
      target.checked = true;
      target.dispatchEvent(evt);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.el.classList.remove('no-transition');
        });
      });
      return;
    }

    // Disable transitions so submenu snaps into place instantly
    this.el.classList.add('no-transition');
    
    // Open the submenu BEFORE opening the drawer so it's already in position
    const evt = new Event('change');
    target.checked = true;
    target.dispatchEvent(evt);

    // Re-enable transitions after submenu is positioned
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.el.classList.remove('no-transition');
        // Now open the drawer with the submenu already positioned
        Woolman.ModalsAndDrawers.showModalOrDrawer(this.el.getAttribute('id'));
      });
    });
  }

  toggleSubPanel(event) {
    this.closeAllItems();

    const button = event.currentTarget;
    const targetSelector = button.getAttribute('data-drawer-submenu-close');
    if (!targetSelector) {
      return;
    }

    const input = document.getElementById(targetSelector);
    if (!input) {
      return;
    }

    input.focus();
  }
  
  updateMainContentPanelState() {
    // Check if elements exist
    const panel = this.mainContentPanelAccordion;
    const checkbox = this.mainContentPanelCheckbox;
    if (!this.mainLevel || !panel || !checkbox) {
      return;
    }

    // Check if drawer is open and whether any submenu (excluding main content) is open
    const hasOpenSubmenus = Array.from(this.mainLevel.querySelectorAll('input:checked'))
      .some((cb) => cb !== this.mainContentPanelCheckbox);
    const isDrawerOpen = this.el.classList.contains('is-open') || this.el.hasAttribute('open');

    // If drawer is open but no submenus are open, show main content panel
    if (isDrawerOpen && !hasOpenSubmenus) {
      panel.classList.add('is-open');
      checkbox.checked = true;
      // Ensure styling reflects that no submenu is open
      this.mainLevel.classList.remove('has-open-submenu');
    } else if (hasOpenSubmenus) {
      // If submenus are open, ensure main content panel is closed
      panel.classList.remove('is-open');
      checkbox.checked = false;
      this.mainLevel.classList.add('has-open-submenu');
    }
  }
  
  initDrawerStateObserver() {
    // Create observer to watch for changes to drawer state
    this.drawerObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if classes changed
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const newIsOpen = this.el.classList.contains('is-open');
          if (newIsOpen !== this.isOpen) {
            this.isOpen = newIsOpen;
            shouldUpdate = true;
          }
        }
        
        // Check if open attribute changed
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const newIsOpen = this.el.hasAttribute('open');
          if (newIsOpen !== this.isOpen) {
            this.isOpen = newIsOpen;
            shouldUpdate = true;
          }
        }
      });
      
      // If state changed, update the main content panel
      if (shouldUpdate) {
        this.updateMainContentPanelState();
      }
    });
    
    // Start observing the drawer element
    this.drawerObserver.observe(this.el, {
      attributes: true,
      attributeFilter: ['class', 'open']
    });
  }
  
  // Clean up observer when element is removed
  disconnectedCallback() {
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
  }
}

if (customElements.get('drawer-menu') === undefined) {
  customElements.define('drawer-menu', DrawerMenu);
}
